import { ApolloCache } from '@apollo/client';
import { IMembersResults } from '../member/member.types';
import { GET_MEMBERS } from '../member/member.queries';
import { GET_COMPANY_INVITES } from '../invite/invite.queries';
import { ICompanyOverview, INVITE_STATUS, TEMPLATE_TYPE } from '../../../lib/types';
import { ICompanyInviteResult } from '../invite/invite.types';
import { ICurrCompanyRequestTypesResults, ISelectedRequestTypesResults } from '../request-type/request-type.types';
import { ICompanyLocationsResult } from '../locations/location.types';
import { ICompanyDepartmentsResult } from '../departments/department.types';
import { GET_COMPANY_DEPARTMENTS } from '../departments/department.queries';
import { GET_COMPANY_LOCATIONS } from '../locations/location.queries';
import { GET_COMPANY_REQUEST_TYPES, GET_SELECTED_REQUEST_TYPES } from '../request-type/request-type.queries';
import toast from 'react-hot-toast';
import { COMPLETE_ONBOARDING_KEY } from '../../../lib/const';
import { IUserCompaniesResult } from './company.types';
import { IArchivableProps } from '../../../lib/query.types';
import { GET_USER_COMPANIES } from './company.queries';
import { GET_ENTITIES } from '../entities/entity.queries';
import { IEntitiesResults } from '../entities/entity.types';
import { IClientsResults } from '../clients/client.types';
import { GET_CLIENTS } from '../clients/client.queries';
import { GET_CLIENT_INVITES } from '../client-invite/client-invite.queries';
import { IClientInviteQueryParams, IClientInvitesResult } from '../client-invite/client-invite.types';
import { IListRequestAsStaffResults, IListRequestAsStaffVariables } from '../request/request.types';
import { LIST_REQUESTS_AS_STAFF } from '../request/request.queries';
import { GET_TEMPLATES } from '../template/template.queries';
import { IGetTemplatesParams, ITemplateResults } from '../template/template.types';
import { GET_REQUEST_GROUPS } from '../request-group/request-group.queries';
import { IGetRequestGroupsResults } from '../request-group/request-group.types';
import { DEFAULT_LIMIT } from '../request/request.consts';

export const readUserCompaniesCache = (cache: ApolloCache<unknown>) =>
  cache.readQuery<IUserCompaniesResult, IArchivableProps>({
    query: GET_USER_COMPANIES,
    variables: { includeArchived: true }
  }) ?? { companies: [] };

export const wipeCurrCompanyCache = (cache: ApolloCache<unknown>) => {
  toast.dismiss(COMPLETE_ONBOARDING_KEY);

  cache.writeQuery<ICompanyDepartmentsResult, IArchivableProps>({
    query: GET_COMPANY_DEPARTMENTS,
    data: { departments: [] },
    variables: { includeArchived: true },
    overwrite: true
  });
  cache.writeQuery<ICompanyLocationsResult, IArchivableProps>({
    query: GET_COMPANY_LOCATIONS,
    data: { locations: [] },
    variables: { includeArchived: true },
    overwrite: true
  });

  cache.writeQuery({
    query: GET_COMPANY_REQUEST_TYPES,
    data: { companyRequestTypes: [] } as ICurrCompanyRequestTypesResults,
    overwrite: true
  });
  cache.writeQuery({
    query: GET_SELECTED_REQUEST_TYPES,
    data: { requestTypes: [] } as ISelectedRequestTypesResults,
    overwrite: true
  });
  cache.writeQuery({ query: GET_MEMBERS, data: { members: [] } as IMembersResults, overwrite: true });
  cache.writeQuery({
    query: GET_COMPANY_INVITES,
    data: { invites: [] } as ICompanyInviteResult,
    variables: { status: INVITE_STATUS.PENDING },
    overwrite: true
  });

  cache.writeQuery<IEntitiesResults, IArchivableProps>({
    query: GET_ENTITIES,
    data: { entities: [] },
    variables: { includeArchived: true },
    overwrite: true
  });

  cache.writeQuery<IClientsResults, IArchivableProps>({
    query: GET_CLIENTS,
    data: { clients: [] },
    variables: { includeArchived: true },
    overwrite: true
  });
  cache.writeQuery<IClientInvitesResult, IClientInviteQueryParams>({
    query: GET_CLIENT_INVITES,
    data: { clientInvites: [] },
    variables: { status: INVITE_STATUS.PENDING },
    overwrite: true
  });

  cache.writeQuery<ITemplateResults, IGetTemplatesParams>({
    query: GET_TEMPLATES,
    data: { templates: [] },
    variables: { includeArchived: false, types: [TEMPLATE_TYPE.REQUEST] },
    overwrite: true
  });
  cache.writeQuery<ITemplateResults, IGetTemplatesParams>({
    query: GET_TEMPLATES,
    data: { templates: [] },
    variables: { includeArchived: false, types: [TEMPLATE_TYPE.BLOCK, TEMPLATE_TYPE.SECTION] },
    overwrite: true
  });
  cache.writeQuery<ITemplateResults, IGetTemplatesParams>({
    query: GET_TEMPLATES,
    data: { templates: [] },
    variables: { types: [TEMPLATE_TYPE.BLOCK, TEMPLATE_TYPE.REQUEST, TEMPLATE_TYPE.SECTION] },
    overwrite: true
  });

  cache.writeQuery<IGetRequestGroupsResults, IArchivableProps>({
    query: GET_REQUEST_GROUPS,
    data: { requestGroups: [] },
    variables: { includeArchived: true },
    overwrite: true
  });

  cache.writeQuery<IListRequestAsStaffResults, IListRequestAsStaffVariables>({
    query: LIST_REQUESTS_AS_STAFF,
    data: {
      listRequestsAsStaff: {
        page: 1, total: 0, requests: [],
        includeArchived: false,
        status: []
      }
    },
    variables: { limit: DEFAULT_LIMIT },
    overwrite: true
  });

  // Evict all company-specific cached entities to ensure no stale data from previous organization
  // This clears individual item queries (e.g., GET_REQUEST, GET_ENTITY) that aren't in refetch queries
  const fieldsToEvict = [
    'company',
    'requestById',
    'requestByWithToken',
    'requestGroup',
    'requestGroupOverview',
    'entity',
    'client',
    'template',
    'invite',
    'clientInvite',
    'listRequestsAsStaff'
  ];

  fieldsToEvict.forEach(fieldName => {
    cache.evict({ id: 'ROOT_QUERY', fieldName });
  });

  // Evict all entities of specific types by examining the cache
  const cacheSnapshot = cache.extract();
  const entityTypesToEvict = ['Request', 'RequestGroup', 'Client', 'Entity', 'Template', 'Invite', 'ClientInvite', 'Member'];

  Object.keys(cacheSnapshot).forEach(key => {
    // Apollo cache keys are formatted as "TypeName:id"
    const [typeName] = key.split(':');
    if (entityTypesToEvict.includes(typeName)) {
      cache.evict({ id: key });
    }
  });

  // Garbage collect to remove orphaned cache entries
  cache.gc();
};

export const writeUserCompaniesCache = (cache: ApolloCache<unknown>, companies: ICompanyOverview[]) => {
  cache.writeQuery<IUserCompaniesResult, IArchivableProps>({
    query: GET_USER_COMPANIES,
    data: { companies },
    variables: { includeArchived: true }
  });
};
